/**
 * Mock implementation of `InfraService`, with the same signatures as the real API will have.
 *
 * Where real data already exists in the app it is reused, so the mock agrees with what the rest of the wizard knows:
 *  - Traverse in Production: the production catalog (regions, resource groups, VNets, subnets with free IPs, the 24
 *    PMTTRAVDB servers). The default VNet is never offered.
 *  - Ross: its provisioned regions and the resources the pipeline resolves for them, including readiness issues.
 * Every other product and environment gets plausible generated data, so the whole flow can be exercised.
 */
import { AZURE_DISK_SKUS, DEFAULT_SQL_VM } from '@/data/azureDiskSkus'
import { AZURE_REGIONS, AZURE_REGION_CODES } from '@/data/azureRegions'
import { ENVIRONMENTS, INFRA_ENVIRONMENTS } from '@/data/environments'
import { isProcessPro, isTraverse, productFamily, productLineOf } from '@/data/productLines'
import { getRossRegionResources } from '@/data/rossRegionResources'
import type { InfraService } from '@/services/infraService'
import type {
  ExistingEnvironment,
  ExistingServers,
  ExistingSqlServer,
  ImageComponent,
  InfraEnvironment,
  InfraImage,
  InfraProduct,
  InfraRegion,
  InfraResourceGroup,
  InfraSubmitResult,
  InfraVnet,
  IisServer,
  PublicIpQuota,
  PublicIpQuotaQuery,
  RdsMachineInfo,
  SqlServerNetworkAndImage,
  SqlStorageRecommendation,
  VmSizes,
  VmSizesQuery,
} from '@/types/infrastructure'
import { uncheckedVmSizes as unchecked } from '@/utils/vmSizes'
import { nextSequence, productKey, serverNameSeries, type ServerSequence } from '@/utils/naming'
import {
  getLocations,
  getProductResources,
  getResourceGroups,
  getSubnets,
  getVnets,
  subnetFreeIps,
} from '@/utils/traverseProduction'

const PRODUCTS: InfraProduct[] = [
  { slug: 'traverse', name: 'Traverse' },
  { slug: 'process-pro', name: 'Process Pro' },
  { slug: 'ross', name: 'Ross' },
  { slug: 'appcentral', name: 'AppCentral' },
]

const NON_PROD = ['dev', 'stg', 'qa', 'uat', 'demo']
const NON_PROD_REGIONS = ['East US 2', 'Central US', 'North Central US', 'West US 2', 'South Central US']
const ALL_REGIONS = [...AZURE_REGIONS, 'South Central US']

/** Short environment code used in generated names, matching the building-blocks convention (prd, stg, tst). */
function envShort(environmentId: string): string {
  if (environmentId === 'production' || environmentId === 'prod_usgov') return 'prd'
  if (environmentId === 'stg') return 'stg'
  if (environmentId === 'dev') return 'dev'
  return 'tst'
}

function productShort(product: InfraProduct): string {
  if (isTraverse(product)) return 'trav'
  if (isProcessPro(product)) return 'ppg'
  return productKey(product).slice(0, 5) || 'prod'
}

/**
 * What the production data catalog is keyed by: the product family for a product line (both Traverse lines share Traverse's
 * data), the plain product key for any other product.
 */
const catalogKey = (product: InfraProduct): string => productFamily(product) ?? productKey(product)

const idFor = (name: string) => name

/**
 * Golden images per product line, and per product for the products that are not one. A per-component list can be plugged in
 * later by filling `COMPONENT_IMAGES`; until then Advanced Analytics and Bartender use the same list as the main server, as
 * agreed. The Traverse 11.2 Global names are placeholders: each line has its own images, and the real names come with the API.
 */
const MAIN_IMAGES: Record<string, string[]> = {
  'traverse-11.0-standard': ['Traverse Windows Server 2022 – 2026.10', 'Traverse Windows Server 2022 – 2026.07', 'Traverse Windows Server 2019 – 2025.12'],
  'traverse-11.2-global': ['Traverse 11.2 Global Windows Server 2022 – 2026.10', 'Traverse 11.2 Global Windows Server 2022 – 2026.07'],
  'process-pro-global': ['Process Pro Windows Server 2022 – 2026.10', 'Process Pro Windows Server 2022 – 2026.06'],
  ross: ['Ross Windows Server 2022 – 2026.09', 'Ross Windows Server 2019 – 2026.01'],
  appcentral: ['AppCentral Windows Server 2022 – 2026.10', 'AppCentral Windows Server 2022 – 2026.08'],
}
const imageKey = (product: InfraProduct): string => productLineOf(product)?.id ?? productKey(product)
const COMPONENT_IMAGES: Partial<Record<ImageComponent, Record<string, string[]>>> = {
  // advancedAnalytics: { traverse: ['...'] },   <- component-specific image names go here when they are supplied
  // bartender: { traverse: ['...'] },
}

function imagesFor(product: InfraProduct, component: ImageComponent): InfraImage[] {
  const key = imageKey(product)
  const names =
    COMPONENT_IMAGES[component]?.[key] ?? MAIN_IMAGES[key] ?? [`${product.name} golden image 2026.10`, `${product.name} golden image 2026.08`]
  return names.map((name) => ({ id: idFor(name), name, osVersion: name.includes('2019') ? 'Windows Server 2019' : 'Windows Server 2022' }))
}

// ─── Locations ───────────────────────────────────────────────────────────────

function regionsFor(environmentId: string, product: InfraProduct): InfraRegion[] {
  const catalog = getProductResources(environmentId, catalogKey(product))
  if (catalog) {
    return getLocations(catalog).map(({ code, label }) => ({ id: code, label, issues: [] }))
  }
  if (productKey(product) === 'ross') {
    return Object.entries(getRossRegionResources(environmentId)).map(([label, resource]) => ({
      id: resource.location,
      label,
      issues: resource.issues,
    }))
  }
  const labels = NON_PROD.includes(environmentId) ? NON_PROD_REGIONS : ALL_REGIONS
  return labels.map((label) => ({ id: AZURE_REGION_CODES[label] ?? label.toLowerCase().replace(/\s+/g, ''), label, issues: [] }))
}

function resourceGroupsFor(environmentId: string, regionId: string, product: InfraProduct): InfraResourceGroup[] {
  const catalog = getProductResources(environmentId, catalogKey(product))
  if (catalog) return getResourceGroups(catalog, regionId).map((name) => ({ id: idFor(name), name }))
  if (productKey(product) === 'ross') {
    const found = Object.values(getRossRegionResources(environmentId)).find((resource) => resource.location === regionId)
    return found ? [{ id: found.rg, name: found.rg }] : []
  }
  const env = envShort(environmentId)
  return [`rg-${regionId}-${env}-${productShort(product)}`, `rg-${regionId}-${env}-shared`].map((name) => ({ id: idFor(name), name }))
}

function vnetsFor(environmentId: string, regionId: string, resourceGroupId: string, product: InfraProduct): InfraVnet[] {
  const catalog = getProductResources(environmentId, catalogKey(product))
  if (catalog) {
    // getVnets never returns the default VNet, so an environment with only that one has none to offer.
    return getVnets(catalog, regionId, resourceGroupId).map((vnet) => ({
      id: idFor(vnet.name),
      name: vnet.name,
      addressSpace: vnet.addressSpace,
      subnets: getSubnets(catalog, regionId, resourceGroupId, vnet.name).map((subnet) => ({
        id: idFor(subnet.name),
        name: subnet.name,
        prefix: subnet.prefix,
        freeIps: subnetFreeIps(subnet),
      })),
    }))
  }
  if (productKey(product) === 'ross') {
    const found = Object.values(getRossRegionResources(environmentId)).find((resource) => resource.location === regionId && resource.rg === resourceGroupId)
    return found
      ? [{ id: found.vnet, name: found.vnet, addressSpace: '', subnets: [{ id: found.subnet, name: found.subnet, prefix: '', freeIps: 100 }] }]
      : []
  }
  const base = resourceGroupId.replace(/^rg-/, '')
  return [
    {
      id: `vnet-${base}01`,
      name: `vnet-${base}01`,
      addressSpace: '10.20.0.0/16',
      subnets: [
        { id: `snet-${base}-01`, name: `snet-${base}-01`, prefix: '10.20.0.0/24', freeIps: 120 },
        { id: `snet-${base}-02`, name: `snet-${base}-02`, prefix: '10.20.1.0/27', freeIps: 12 },
        // A nearly full /28: too small for a large set of RDS VMs, so "not enough space" and the readiness panel's subnet row can be seen.
        { id: `snet-${base}-03`, name: `snet-${base}-03`, prefix: '10.20.2.0/28', freeIps: 5 },
      ],
    },
  ]
}

// ─── Existing servers ────────────────────────────────────────────────────────

const TRAVERSE_SQL_IMAGE = 'SQL Server 2019 Standard on Windows Server 2019 (2025.11)'
const GENERIC_SQL_IMAGE = 'SQL Server 2022 Standard on Windows Server 2022 (2026.04)'

/** Where the Process Pro SQL servers are. These are existing servers, named the old way (PPG<customer>DB<nn>): no generated name continues them. */
const PROCESS_PRO_SERVERS: { name: string; environment: string; region: string; instances: string[] }[] = [
  { name: 'PPG101DB01', environment: 'production', region: 'northcentralus', instances: ['PPG101', 'PPG101REPORT'] },
  { name: 'PPG101DB02', environment: 'production', region: 'northcentralus', instances: ['PPG101B'] },
  { name: 'PPG204DB01', environment: 'production', region: 'northcentralus', instances: ['PPG204'] },
  { name: 'PPG150DB01', environment: 'production', region: 'eastus2', instances: ['PPG150'] },
  { name: 'PPG310DB01', environment: 'stg', region: 'eastus2', instances: ['PPG310'] },
  { name: 'PPG900DB01', environment: 'dev', region: 'centralus', instances: ['PPG900'] },
]

function existingServers(product: InfraProduct, environmentId: string, regionId: string): ExistingSqlServer[] {
  const key = productKey(product)
  const catalog = getProductResources(environmentId, catalogKey(product))
  if (catalog) {
    return catalog.sqlServers
      .filter((server) => server.region === regionId)
      .map((server) => ({
        name: server.name,
        resourceGroup: server.resourceGroup,
        image: TRAVERSE_SQL_IMAGE,
        vnet: 'vnet-northcentralus-prd-trav',
        subnet: 'snet3-northcentralus-prd-trav',
        instances: server.existingInstances,
      }))
  }
  const env = envShort(environmentId)
  if (isProcessPro(product)) {
    // Process Pro servers exist in specific places, and every name is unique, as real server names are.
    const here = PROCESS_PRO_SERVERS.filter((entry) => entry.environment === environmentId && entry.region === regionId)
    return here.map((entry) => ({
      name: entry.name,
      resourceGroup: `rg-${regionId}-${env}-ppg`,
      image: GENERIC_SQL_IMAGE,
      vnet: `vnet-${regionId}-${env}-ppg01`,
      subnet: `snet-${regionId}-${env}-ppg-01`,
      instances: entry.instances,
    }))
  }
  if (key === 'ross') {
    const found = Object.values(getRossRegionResources(environmentId)).find((resource) => resource.location === regionId)
    if (!found) return []
    return [{ name: found.sql, resourceGroup: found.rg, image: GENERIC_SQL_IMAGE, vnet: found.vnet, subnet: found.subnet, instances: ['ROSSAPP'] }]
  }
  return [
    {
      name: `sqlsrv-${regionId}-${env}-${productShort(product)}-001`,
      resourceGroup: `rg-${regionId}-${env}-${productShort(product)}`,
      image: GENERIC_SQL_IMAGE,
      vnet: `vnet-${regionId}-${env}-${productShort(product)}01`,
      subnet: `snet-${regionId}-${env}-${productShort(product)}-01`,
      instances: ['MSSQLSERVER2022', 'REPORTING'],
    },
  ]
}

function rdsMachineFor(product: InfraProduct, environmentId: string, regionId: string, resourceGroupId: string): RdsMachineInfo {
  const key = productKey(product)
  const server = existingServers(product, environmentId, regionId)[0]
  const name = isTraverse(product) ? 'PTRAV101RDS01' : isProcessPro(product) ? 'PPG101RDS01' : key === 'ross' ? 'PROSSSAASAP101' : `${productShort(product).toUpperCase()}RDS01`
  return {
    name,
    size: 'Standard_D8s_v5',
    os: 'Windows Server 2022 Datacenter',
    image: imagesFor(product, 'main')[0].name,
    vnet: server?.vnet ?? `vnet-${regionId}-${envShort(environmentId)}-${productShort(product)}01`,
    subnet: server?.subnet ?? `snet-${regionId}-${envShort(environmentId)}-${productShort(product)}-01`,
    ip: resourceGroupId ? '10.20.0.14' : '',
  }
}

// ─── Existing servers of one resource group (AppCentral level, existing environment) ──

/**
 * The RDS and Azure SQL servers of a resource group and region. The mock knows when none of them was created, so the latest is the
 * highest-numbered one. A group with no SQL server has no RDS server either, which gives the empty state.
 */
function existingServersIn(product: InfraProduct, environmentId: string, regionId: string, resourceGroupId: string): ExistingServers {
  const sql = existingServers(product, environmentId, regionId).filter((server) => server.resourceGroup === resourceGroupId)
  if (sql.length === 0) return { rdsServers: [], sqlServers: [] }
  const rds = rdsMachineFor(product, environmentId, regionId, resourceGroupId)
  return {
    rdsServers: [{ name: rds.name, resourceGroup: resourceGroupId, region: regionId, image: rds.image, vnet: rds.vnet, subnet: rds.subnet, ip: rds.ip, size: rds.size, os: rds.os }],
    sqlServers: sql.map((server) => ({ name: server.name, resourceGroup: server.resourceGroup, region: regionId, image: server.image, vnet: server.vnet, subnet: server.subnet })),
  }
}

// ─── Server names that already follow the naming rule (utils/naming.ts) ──────

/**
 * Servers named the current way (`PMTTRAVRDS38`: a prefix, the series and a number), so the sequence can be seen continuing from the
 * highest number and skipping a gap, in any case. Everything else the mock lists has an older name, which no series matches.
 */
const NAMED_SERVERS: Record<string, string[]> = {
  production: ['PMTTRAVRDS01', 'pmttravrds38', 'PMTTRAVRDS07', 'PTRAV07APP01', 'PTRAV03APP01'],
  stg: ['STGTRAVRDS01', 'STGTRAVRDS02'],
  demo: ['DMTTRAVDB02'],
}

/** Every server name in an environment ("the whole subscription"): the named ones, the database servers, the RDS machines and the IIS servers. */
function namesIn(environmentId: string): string[] {
  const names = [...(NAMED_SERVERS[environmentId] ?? [])]
  for (const product of PRODUCTS) {
    for (const region of regionsFor(environmentId, product)) {
      names.push(...existingServers(product, environmentId, region.id).map((server) => server.name))
      names.push(rdsMachineFor(product, environmentId, region.id, '').name)
    }
  }
  names.push(...IIS_SERVERS.filter((server) => server.environment === environmentId).map((server) => server.name))
  return names
}

// ─── Existing IIS servers ────────────────────────────────────────────────────

/** Where IIS servers exist: one place has two, one has exactly one, and everywhere else has none, so every state can be shown. */
const IIS_SERVERS: { name: string; environment: string; region: string; resourceGroup: string }[] = [
  { name: 'PIISNCUS01', environment: 'production', region: 'northcentralus', resourceGroup: 'rg-northcentralus-prd-iis' },
  { name: 'PIISNCUS02', environment: 'production', region: 'northcentralus', resourceGroup: 'rg-northcentralus-prd-iis' },
  { name: 'PIISEUS201', environment: 'production', region: 'eastus2', resourceGroup: 'rg-eastus2-prd-iis' },
  { name: 'DIISCUS01', environment: 'dev', region: 'centralus', resourceGroup: 'rg-centralus-dev-iis' },
]

function existingIisServers(environmentId: string, regionId: string): IisServer[] {
  return IIS_SERVERS.filter((server) => server.environment === environmentId && server.region === regionId).map((server, position) => ({
    id: `iis-${server.name.toLowerCase()}`,
    name: server.name,
    resourceGroup: server.resourceGroup,
    image: 'Windows Server 2022 Datacenter with IIS 10 (2026.09)',
    vnet: `vnet-${regionId}-${envShort(environmentId)}-iis01`,
    subnet: `snet-${regionId}-${envShort(environmentId)}-iis-01`,
    ip: `10.30.${position}.21`,
  }))
}

// ─── The service ─────────────────────────────────────────────────────────────

export interface MockOptions {
  /** Artificial delay per call, to exercise loading states. Defaults to none under test and 150 ms otherwise. */
  latencyMs?: number
}

export function createMockInfraService(options: MockOptions = {}): InfraService {
  const latency = options.latencyMs ?? (import.meta.env.MODE === 'test' ? 0 : 150)
  const later = async <T>(value: () => T): Promise<T> => {
    if (latency > 0) await new Promise((resolve) => setTimeout(resolve, latency))
    return value()
  }
  let submitted = 0

  return {
    getProducts: () => later(() => PRODUCTS.map((product) => ({ ...product }))),

    getEnvironments: () => later((): InfraEnvironment[] => INFRA_ENVIRONMENTS.map((env) => ({ id: env.value, label: env.label }))),

    getRegions: (environmentId, product) => later(() => regionsFor(environmentId, product)),

    getResourceGroups: (environmentId, regionId, product) => later(() => resourceGroupsFor(environmentId, regionId, product)),

    getVnets: (environmentId, regionId, resourceGroupId, product) => later(() => vnetsFor(environmentId, regionId, resourceGroupId, product)),

    getGoldenImages: (product, _regionId, component = 'main') => later(() => imagesFor(product, component)),

    getExistingEnvironment: (product, environmentId, regionId, resourceGroupId): Promise<ExistingEnvironment> =>
      later(() => ({
        rdsServer: rdsMachineFor(product, environmentId, regionId, resourceGroupId),
        sqlServers: existingServers(product, environmentId, regionId),
      })),

    getExistingSqlServerNetworkAndImage: (product, serverName): Promise<SqlServerNetworkAndImage> =>
      later(() => {
        const all = ENVIRONMENTS.flatMap((env) => {
          const regions = regionsFor(env.value, product)
          return regions.flatMap((region) => existingServers(product, env.value, region.id))
        })
        const found = all.find((server) => server.name.toLowerCase() === serverName.toLowerCase())
        if (!found) throw new Error(`SQL server ${serverName} was not found.`)
        return { image: found.image, vnet: found.vnet, subnet: found.subnet }
      }),

    getExistingServers: (product, environmentId, regionId, resourceGroupId) =>
      later(() => existingServersIn(product, environmentId, regionId, resourceGroupId)),

    getNextServerSequence: (environmentId, product, role): Promise<ServerSequence> =>
      later(() => {
        const series = serverNameSeries({ product, environmentId, role })
        if (!series.ok) throw new Error(series.problem)
        return nextSequence(namesIn(environmentId), series.series)
      }),

    getExistingIisServers: (environmentId, regionId) => later(() => existingIisServers(environmentId, regionId)),

    getSqlServerNames: (product, environmentId) =>
      later(() => {
        const names = regionsFor(environmentId, product).flatMap((region) => existingServers(product, environmentId, region.id).map((server) => server.name))
        return [...new Set(names)]
      }),

    // The same VM and disk sizes for every product and region for now. A real API would size the VM for the product.
    getSqlStorageRecommendation: (): Promise<SqlStorageRecommendation> =>
      later(() => ({ vm: { ...DEFAULT_SQL_VM }, skus: AZURE_DISK_SKUS.map((sku) => ({ ...sku })) })),

    // The mock cannot ask Azure what a region offers, so every size is unchecked and built as the catalog shows it.
    getVmSizes: (query: VmSizesQuery): Promise<VmSizes> =>
      later(() => ({
        region: query.regionId,
        checked: false,
        quotaChecked: false,
        note: '',
        sizes: unchecked(query),
      })),

    // The mock cannot read a subscription's usage, so it never says a public IP is short: nothing was checked, and nothing is blocked.
    getPublicIpQuota: (query: PublicIpQuotaQuery): Promise<PublicIpQuota> =>
      later(() => ({ region: query.regionId, checked: false, failed: false, used: null, limit: null, available: null, needed: query.count, sufficient: true, message: '' })),

    // The same latest image for every product for now. A real API would return it per product.
    getLatestSqlImage: (): Promise<InfraImage> =>
      later(() => ({
        id: 'sql2022-ent-win2022-2026.10',
        name: 'SQL Server 2022 Enterprise on Windows Server 2022 (2026.10)',
        osVersion: 'Windows Server 2022',
      })),

    submit: (): Promise<InfraSubmitResult> =>
      later(() => {
        submitted += 1
        return { reference: `INFRA-MOCK-${String(submitted).padStart(4, '0')}`, acceptedAt: new Date().toISOString() }
      }),
  }
}
